import { Box, Flex, Text } from 'components/elements'
import { ReactNode, forwardRef } from 'react'
import { FieldError } from 'react-hook-form'
import { CSS, styled } from 'theme'

interface ToggleProps {
  disabled?: boolean
  name: string
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  ref: React.RefObject<HTMLInputElement>
  value?: boolean
  css?: CSS
  size?: 'sm' | 'md'
  label?: string
  fieldLabel?: string
  error?: FieldError | string
  boxCss?: CSS
  required?: boolean
}

const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  (
    {
      name,
      value,
      disabled,
      css,
      size,
      label,
      fieldLabel,
      error,
      boxCss,
      required,
      ...rest
    },
    ref
  ) => {
    return (
      <Box css={{ mb: label ? '$20' : '', pr: '$space$12', ...boxCss }}>
        {fieldLabel ? (
          <Text as="label" css={{ mb: '$10', display: 'inline-block' }}>
            {fieldLabel}
            {required ? (
              <Text css={{ color: '$error', ml: '$space$6' }}>*</Text>
            ) : null}
          </Text>
        ) : null}

        <Flex>
          <ToggleBox ref={ref} size={size} css={{ ...css }}>
            <input
              type="checkbox"
              id={name}
              name={name}
              checked={value}
              disabled={disabled}
              {...rest}
            />
            <Text as="label" htmlFor={name} children={null} />
          </ToggleBox>
          {label && (
            <Text as="label" css={{ mb: 0, pl: '$space$8' }}>
              {label}
            </Text>
          )}
        </Flex>
        {!!error && (
          <Text color="error" className="errorField">
            {error as ReactNode}
          </Text>
        )}
      </Box>
    )
  }
)

export default Toggle

export const ToggleBox = styled(Box, {
  'label': {
    'position': 'relative',
    'display': 'block',
    'borderRadius': '$radii$xl',
    'userSelect': 'none',
    'm': 0,
    'transition': 'all 0.4s $transition$easeOutQuint',
    'cursor': 'pointer',

    '&:after': {
      content: '',
      display: 'block',
      background: '$whiteBase',
      borderRadius: '$radii$rounded',
      boxShadow: '0 0 0 1px $blackBase',
      position: 'absolute',

      transition: 'all 0.4s $transition$easeOutBack',
    },
  },
  'input': {
    'position': 'absolute',
    'opacity': 0,
    '&:checked': {
      '& ~ label': {
        background: '$primaryBg',
      },
    },
    '&:disabled': {
      '& ~ label': {
        backgroundColor: '$primaryDisabled',
        color: '$disabledText',
        cursor: 'not-allowed',
        borderColor: '$primaryDisabled',
      },
    },
  },
  '&:not(:checked)': {
    label: {
      'background': '$gray300',
      '&:after': {
        left: 2,
      },
    },
  },
  'variants': {
    size: {
      sm: {
        label: {
          'height': '$size$20',
          'width': '$size$36',
          '&:after': {
            height: '$size$16',
            width: '$size$16',
            top: 2,
            left: 1,
          },
        },
        input: {
          '&:checked': {
            '& ~ label': {
              '&:after': {
                left: '$size$18',
              },
            },
          },
        },
      },
      md: {
        label: {
          'height': '$size$24',
          'width': '$size$44',
          '&:after': {
            height: '$size$20',
            width: '$size$20',
            top: 2,
            left: 3,
          },
        },
        input: {
          '&:checked': {
            '& ~ label': {
              '&:after': {
                left: '$size$22',
              },
            },
          },
        },
      },
    },
  },
  'defaultVariants': {
    size: 'md',
  },
})
